/* eslint-disable @typescript-eslint/naming-convention */
/* eslint-disable no-shadow */
import classNames from 'classnames';
import {IInputProps as IInputProps} from 'components/Input';
import React, {FC, memo, ReactNode, useId, useState} from 'react';
import Select, {ActionMeta, ControlProps, OptionProps} from 'react-select';
import style from './style.module.scss';
import {useSelectComponents} from './hooks/useSelectComponents';
import {useInputSelectLogic} from './hooks/useInputSelectLogic';
import {CustomControl, DropdownIndicator, FilterOverlay, MultiValueContainer} from './components';

export interface ISelectOption<T> {
    label: string;
    value: T;
    isDisabled?: boolean;
}

type TSelectInputProps<V extends string = string> = Pick<
    IInputProps<V>,
    'captionText' | 'errorText' | 'labelText' | 'placeholder' | 'name' | 'disabled'
>;

export interface ISelectProps<T, M extends string = string> extends TSelectInputProps<M> {
    className?: string;
    options?: ISelectOption<T>[];
    defaultOption?: ISelectOption<T> | ISelectOption<T>[];
    onChange?: (value: ISelectOption<T>) => void;
    onChangeMulti?: (value: ISelectOption<T>[]) => void;
    value?: ISelectOption<T> | ISelectOption<T>[];
    isLoading?: boolean;
    emptyMessage?: string;
    variant?: 'default' | 'filter';
    dropDownIndicatorIcon?: ReactNode;
    isSearchable?: boolean;
    loadOptions?: (inputValue: string, callback: (options: ISelectOption<T>[]) => void) => void;
    isMobileCompactView?: boolean;
    multiple?: boolean;
    onClear?: () => void;
    onApply?: () => void;
    isFilterSelect?: boolean;
    controlInfoTooltip?: ReactNode;
    customOption?: FC<OptionProps>;
    customControl?: FC<ControlProps>;
    onInputValueChange?: (value: string) => void;
    showMobileClearButton?: boolean;
    dataAutotestTarget?: string;
    dataAutotestTargetId?: string;
    resetBtnDataAutotestTarget?: string;
    resetBtnDataAutotestId?: string;
    inputClassName?: string;
    resetBtnClassName?: string;
    suggestionItemClassName?: string;
    suggestionDataAutotestTarget?: string;
    suggestionDataAutotestId?: string;
    key?: string;
    menuIsOpen?: boolean;
}

const InputSelectOrigin = <T, M extends string = string>({
    className,
    name,
    onChange,
    value,
    defaultOption,
    options,
    isLoading,
    labelText,
    captionText,
    errorText,
    placeholder,
    disabled,
    emptyMessage,
    variant = 'default',
    dropDownIndicatorIcon,
    isSearchable = false,
    loadOptions,
    multiple = false,
    onApply,
    onClear,
    isFilterSelect = false,
    controlInfoTooltip,
    customOption,
    customControl,
    key,
    menuIsOpen,
    ...props
}: ISelectProps<T, M>) => {
    const id = useId();
    // const isMobile = useIsMobileByScreen();
    const [isMenuOpen, setIsMenuOpen] = useState(false);

    const {
        selectRef,
        isFilled,
        isFocused,
        showOverlay,
        // minWidth,
        handleSelectChange,
        handleSelectOnFocus,
        handleSelectOnBlur,
        handleClearValue,
        handleApply,
        handleOverlayClick,
        // setMinWidth,
    } = useInputSelectLogic({
        defaultOption,
        onClear,
        onApply,
        isFilterSelect,
        onChange,
        isMenuOpen,
    });

    const {caption, list, option, noOptionsMessage} = useSelectComponents({
        captionText,
        disabled,
        emptyMessage,
        errorText,
        loadOptions,
        multiple,
        onMultipleApply: handleApply,
        onMultipleClear: handleClearValue,
        // setMenuListRef,
    });

    // useEffect(() => {
    //     if (menuListRef && isFocused && !isMobile && isBrowserContext()) {
    //         setMinWidth(menuListRef.getBoundingClientRect().width);
    //     }
    // }, [isFocused, isMobile, menuListRef, setMinWidth]);

    return (
        <div
            className={classNames(style.inputSelect, className, {
                [style.inputSelectValueSelected]: isFilled,
                [style.inputSelectError]: errorText,
                [style.inputSelectDisabled]: disabled,
                [style.inputSelectFocused]: isFocused,
                [style.inputSelectHasLabel]: labelText,
                [style.inputSelectFilterCustom]: 'filter' === variant,
            })}
            // style={{minWidth: `${minWidth}px`}}
            data-label={labelText}
            key={key}
        >
            <Select
                {...props}
                menuShouldScrollIntoView={false}
                instanceId={id}
                className={classNames('shop-select', {[style.multiple]: multiple})}
                classNamePrefix={'shop-select'}
                closeMenuOnSelect={!multiple}
                components={{
                    Control:
                        customControl ||
                        ((props) => (
                            <CustomControl {...props} controlInfoTooltip={controlInfoTooltip} multiple={multiple} />
                        )),
                    DropdownIndicator: (props) => (
                        <DropdownIndicator {...props} dropDownIndicatorIcon={dropDownIndicatorIcon} />
                    ),
                    MenuList: list,
                    MultiValueContainer,
                    Option: customOption || option,
                }}
                defaultValue={defaultOption}
                hideSelectedOptions={false}
                isClearable={false}
                isDisabled={disabled}
                isLoading={isLoading}
                isMulti={multiple}
                isSearchable={isSearchable}
                name={name}
                noOptionsMessage={noOptionsMessage}
                onMenuOpen={() => setIsMenuOpen(true)}
                onMenuClose={() => {
                    setIsMenuOpen(false);
                    // setMinWidth(0);
                }}
                onBlur={handleSelectOnBlur}
                onChange={handleSelectChange as (newValue: unknown, actionMeta: ActionMeta<unknown>) => void}
                onFocus={handleSelectOnFocus}
                options={options}
                placeholder={placeholder}
                ref={selectRef}
                value={value}
                menuIsOpen={menuIsOpen}
            />
            {multiple && isFilterSelect && (
                <FilterOverlay showOverlay={showOverlay} onOverlayClick={handleOverlayClick} />
            )}
            {caption}
        </div>
    );
};

export const InputSelect = memo(InputSelectOrigin) as unknown as typeof InputSelectOrigin;
